import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import ProcessLine from "@/components/ProcessLine";
import TermRichText from "@/components/TermRichText";
import { processSteps, capabilities } from "@/data/capabilities";
import { usePageMeta } from "@/hooks/usePageMeta";

const Approach = () => {
  usePageMeta(
    "Services | Tanren",
    "Five stages and nine disciplines covering the full biomass value chain: feedstock R&D, process engineering, pyrolysis, torrefaction, gasification and CBG systems, carbon credits, MRV and market access."
  );
  return (
  <div data-testid="page-approach">
    <section className="bg-paper text-char-900 pt-40 pb-20 lg:pb-28 border-b border-char-900/10">
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <h1 className="font-display mt-6 max-w-[22ch] text-char-900">
          Five stages. One accountable team.
        </h1>
        <p className="mt-8 max-w-[62ch] text-lg md:text-xl text-char-700 leading-relaxed">
          Whatever stage you're at, we plug in and take you to the next one. Pick one stage or take
          the full journey.
        </p>

        <div className="mt-14">
          <ProcessLine
            steps={processSteps.map((s) => ({
              number: s.number,
              title: s.title,
              href: `#stage-${s.number}`,
            }))}
            variant="light"
            compact
            testId="approach-process-line"
          />
        </div>
      </div>
    </section>

    <section className="bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 divide-y divide-char-900/10">
        {processSteps.map((s, i) => (
          <ScrollReveal key={s.number} delay={i * 40}>
            <div
              id={`stage-${s.number}`}
              data-testid={`approach-step-${s.number}`}
              className="py-16 lg:py-24 grid md:grid-cols-12 gap-10 scroll-mt-28"
            >
              <div className="md:col-span-4">
                <div className="flex items-baseline gap-4">
                  <span className="numeral text-5xl" aria-hidden="true">
                    {s.number}
                  </span>
                </div>
                <h2 className="mt-6 font-display text-char-900">
                  {s.title}
                </h2>
              </div>
              <div className="md:col-span-7 md:col-start-6">
                <p className="text-lg text-char-700 leading-relaxed">{s.description}</p>
                <div className="mt-8">
                  <div className="flex flex-wrap gap-2">
                    {s.services.map((svc) => (
                      <span key={svc} className="chip">
                        <span>
                          <TermRichText text={svc} />
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>

    {/* NINE DISCIPLINES */}
    <section
      id="disciplines"
      data-testid="services-disciplines"
      className="bg-paper border-t border-char-900/10 pt-20 lg:pt-28 scroll-mt-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <h2 className="font-display text-char-900 max-w-[28ch]">
          Nine disciplines. Every one delivered hands-on.
        </h2>
        <p className="mt-8 max-w-[62ch] text-lg text-char-700 leading-relaxed">
          Behind the five stages sit nine engineering and commercial disciplines. Our founders have
          spent years delivering each of them on operating biomass plants. Pick any one, or take the
          full chain.
        </p>

        <div className="mt-14">
          <ProcessLine
            steps={capabilities.map((c) => ({
              number: c.number,
              title: c.title,
              href: `#svc-${c.id}`,
            }))}
            variant="light"
            compact
            testId="services-process-line"
          />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-8 divide-y divide-char-900/10">
        {capabilities.map((cap, i) => (
          <ScrollReveal key={cap.id} delay={i * 40}>
            <div
              id={`svc-${cap.id}`}
              data-testid={`services-block-${cap.id}`}
              className="py-16 lg:py-20 grid md:grid-cols-12 gap-10 scroll-mt-28"
            >
              <div className="md:col-span-4">
                <div className="numeral text-4xl">{cap.number}</div>
                <h3 className="mt-4 font-display text-char-900 text-2xl md:text-3xl">
                  {cap.title}
                </h3>
              </div>
              <div className="md:col-span-7 md:col-start-6">
                <p className="text-lg text-char-700 leading-relaxed">
                  <TermRichText text={cap.tagline} />
                </p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {cap.items.map((it) => (
                    <span key={it} className="chip" data-testid={`chip-${cap.id}-${it}`}>
                      <span>
                        {it === "MRV (via Convergent)" ? (
                          <Link to="/mrv" className="border-b border-dotted border-current/50">
                            {it}
                          </Link>
                        ) : (
                          <TermRichText text={it} />
                        )}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </section>

    <section className="bg-char-900 text-paper py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <h2 className="font-display max-w-[28ch]">
          Whatever stage you're at, we plug in — and take you to the next one.
        </h2>
        <Link to="/contact" className="btn-ember shrink-0" data-testid="approach-cta">
          Start the Conversation
          <ArrowUpRight size={18} />
        </Link>
      </div>
    </section>
  </div>
  );
};

export default Approach;
